import { reportsClient } from "@/api/reportsClient"
import { ActionContext, Module } from "vuex";
import { IEventsState, IRootState, EventPaymentStatus, PaymentEventStreamStatus } from "@/interfaces/state";

interface StartPaymentEventStreamPayload {
  invoiceId: number;
}

interface SseMessage {
  event: EventPaymentStatus;
  payload: Record<string, unknown>;
  timestamp: number;
}

let paymentEventSource: EventSource | null = null;

const eventsModule: Module<IEventsState, IRootState> = {
  state(): IEventsState {
    return {
      paymentEventStreamStatus: "idle",
      paymentEventStreamRetried: false,
      paymentEventInvoiceId: null,
    };
  },

  getters: {
    paymentEventStreamStatus: (state: IEventsState) =>
      state.paymentEventStreamStatus,
    paymentEventStreamRetried: (state: IEventsState) =>
      state.paymentEventStreamRetried,
    paymentEventInvoiceId: (state: IEventsState) =>
      state.paymentEventInvoiceId,
  },

  mutations: {
    setPaymentEventStreamStatus(
      state: IEventsState,
      status: PaymentEventStreamStatus
    ) {
      state.paymentEventStreamStatus = status;
    },

    setPaymentEventStreamRetried(state: IEventsState, retried: boolean) {
      state.paymentEventStreamRetried = retried;
    },

    setPaymentEventInvoiceId(state: IEventsState, invoiceId: number | null) {
      state.paymentEventInvoiceId = invoiceId;
    },

    resetPaymentEventState(state: IEventsState) {
      state.paymentEventStreamStatus = "idle";
      state.paymentEventStreamRetried = false;
      state.paymentEventInvoiceId = null;
    },
  },

  actions: {
    async getSseAuth(
      context: ActionContext<IEventsState, IRootState>
    ) {
      context.commit("setPaymentEventStreamStatus", "authorizing");
      await reportsClient.getSseCookie();
    },

    async startPaymentEventStream(
      context: ActionContext<IEventsState, IRootState>,
      payload: StartPaymentEventStreamPayload
    ) {
      const { invoiceId } = payload;

      context.dispatch("stopPaymentEventStream");
      context.commit("setPaymentEventInvoiceId", invoiceId);
      context.commit("setPaymentEventStreamRetried", false);

      await context.dispatch("getSseAuth");
      context.dispatch("connectPaymentEventStream");
    },

    connectPaymentEventStream(
      context: ActionContext<IEventsState, IRootState>
    ) {
      if (paymentEventSource) {
        paymentEventSource.close();
        paymentEventSource = null;
      }

      context.commit("setPaymentEventStreamStatus", "connecting");

      paymentEventSource = new EventSource(
        `${process.env.VUE_APP_API_URL}api/sse`,
        { withCredentials: true }
      );

      paymentEventSource.onopen = () => {
        context.commit("setPaymentEventStreamStatus", "open");
      };

      paymentEventSource.onmessage = async (rawEvent: MessageEvent) => {
        try {
          const message: SseMessage = JSON.parse(rawEvent.data);
          await context.dispatch("handlePaymentEventMessage", message);
        } catch (error) {
          console.error("Failed to parse payment SSE message", error);
        }
      };

      paymentEventSource.onerror = async (error: Event) => {
        console.error("Payment SSE connection error", error);

        if (paymentEventSource) {
          paymentEventSource.close();
          paymentEventSource = null;
        }

        if (!context.state.paymentEventStreamRetried) {
          context.commit("setPaymentEventStreamRetried", true);

          try {
            await context.dispatch("getSseAuth");
            context.dispatch("connectPaymentEventStream");
            return;
          } catch (retryError) {
            console.error("Failed to re-get SSE auth", retryError);
          }
        }

        context.commit("setPaymentEventStreamStatus", "error");
      };
    },

    async handlePaymentEventMessage(
      context: ActionContext<IEventsState, IRootState>,
      message: SseMessage
    ) {
      if (message.event === "ping") {
        return;
      }

      if (message.event === "pending") {
        return;
      }

      if (message.event === "ttl_expired") {
        context.dispatch("stopPaymentEventStream");
        context.commit("setPaymentEventStreamStatus", "closed");
        context.dispatch("toggleSignUpReportLoadingModal");
        context.dispatch("openNotification", { keypath: "notifications.report_error" })
        return;
      }

      if (message.event === "paid") {
        const matchesInvoice = await context.dispatch(
          "matchesCurrentInvoiceId",
          message
        );
        if (!matchesInvoice) {
          return;
        }

        context.dispatch("stopPaymentEventStream");
        context.dispatch("toggleSignUpReportLoadingModal");
        context.dispatch("toggleSignUpReportSuccessModal");
        return;
      }

      if (message.event === "failed") {
        const matchesInvoice = await context.dispatch(
          "matchesCurrentInvoiceId",
          message
        );
        if (!matchesInvoice) {
          return;
        }

        context.dispatch("stopPaymentEventStream");

        context.dispatch("toggleSignUpReportLoadingModal");
        context.dispatch("toggleSignUpReportFailureModal");
      }
    },

    matchesCurrentInvoiceId(
      context: ActionContext<IEventsState, IRootState>,
      message: SseMessage
    ): boolean {
      if (!context.state.paymentEventInvoiceId) {
        return true;
      }

      const payloadInvoiceId = Number(message.payload?.invoice_id);
      return payloadInvoiceId === context.state.paymentEventInvoiceId;
    },

    stopPaymentEventStream(
      context: ActionContext<IEventsState, IRootState>
    ) {
      if (paymentEventSource) {
        paymentEventSource.close();
        paymentEventSource = null;
      }

      context.commit("setPaymentEventStreamStatus", "closed");
    },

    resetPaymentEventFlow(
      context: ActionContext<IEventsState, IRootState>
    ) {
      context.dispatch("stopPaymentEventStream");
      context.commit("resetPaymentEventState");
    },
  }
};

export default eventsModule
